import { useDispatch, useSelector } from 'react-redux';
import { FileImage, Loader2, Upload, X } from 'lucide-react';
import { ChangeEvent, useState } from 'react';
import Select, { MultiValue } from 'react-select';
import { toast } from 'sonner';
import CreatableSelect from 'react-select/creatable';
import { Input } from '@/components/ui/input';
import {
  Form,
  FormControl,
  FormField,
  FormItem,
  FormLabel,
} from '@/components/ui/form';
import { usePostUploadForm } from '@/redux/post/usePostForm';
import { useGetCategoriesQuery } from '@/redux/category/category.api.slice';
import { ICategoryType } from '@/redux/category/category.d';
import { Textarea } from '@/components/ui/textarea';
import {
  useCreateTagMutation,
  useGetTagsQuery,
} from '@/redux/tag/tag.api.slice';
import { ITagType } from '@/redux/tag/tag.d';
import SectionHeader from '@/components/FullPageCarousel/SectionHeader';
import ErrorMessage from '@/components/ErrorMessage';
import { DropZone } from '@/components/ui/drop-zone';
import { Button } from '@/components/ui/button';
import { imageExtensions } from '@/redux/post/post.schema';
import {
  removeFileFromUploadQueue,
  addFileToUploadQueue,
  selectProjectEditorData,
  setProjectEditorData,
  removeFileFromUploadQueueByCategory,
} from '@/redux/post/post.slice';
import {
  AttachmentParentType,
  AttachmentType,
  IAttachmentCategory,
} from '@/redux/attachment/attachment.d';
import hashFile from '@/lib/xxhash';
import { useDeleteAttachmentMutation } from '@/redux/attachment/attachment.api.slice';

function BasicDetails({ errors }: { errors: { [key: string]: string } }) {
  const projectEditorData = useSelector(selectProjectEditorData);
  const uploadPostForm = usePostUploadForm(projectEditorData);
  const { data: categories, isLoading: isLoadingPostCategories } =
    useGetCategoriesQuery({ type: ICategoryType.POST });

  const {
    data: postTags,
    refetch: refetchTags,
    isLoading: isTagsLoading,
  } = useGetTagsQuery({
    type: ITagType.POST,
  });

  const [createTag] = useCreateTagMutation();
  const [deleteAttachment, { isLoading: isDeleteAttachmentLoading }] =
    useDeleteAttachmentMutation();
  const [preview, setPreview] = useState<string>();
  const dispatch = useDispatch();

  const handleCategoryChange = ({ value }: { value?: string | undefined }) => {
    if (value) {
      dispatch(setProjectEditorData({ ...projectEditorData, category: value }));
    }
  };

  const getPostCategoryOptions = (): {
    label: string;
    value: string;
  } => {
    const options = categories?.map((category) => ({
      label: category.name,
      value: category._id as string,
    }));

    return options as any;
  };

  const getTagOptions = () => {
    const options = postTags?.map((tag) => ({
      label: tag.name,
      value: tag._id as string,
    }));
    return options;
  };

  const handleCreateTag = async (option: string) => {
    try {
      const newTag = await createTag({
        name: option,
        type: ITagType.POST,
      });

      if (newTag?.error) {
        // refactor this
        const { data } = newTag.error as {
          data: {
            message: string;
          };
        };
        throw new Error(data.message);
      }

      await refetchTags();
      const existingOptions = projectEditorData.tags || [];
      dispatch(
        setProjectEditorData({
          ...projectEditorData,
          tags: [...existingOptions, newTag.data?._id as string],
        })
      );
      toast.success('Tag created successfully');
    } catch (e: any) {
      toast.error(e.message);
    }
  };

  const handleTagChange = (
    options: MultiValue<{ label: string; value: string }>
  ) => {
    dispatch(
      setProjectEditorData({
        ...projectEditorData,
        tags: options?.map((option) => option.value),
      })
    );
  };

  const handleInputChange = (
    e: ChangeEvent<HTMLTextAreaElement | HTMLInputElement>
  ) => {
    const { name, value } = e.target;
    dispatch(setProjectEditorData({ ...projectEditorData, [name]: value }));
  };

  const handleFileChange = async (event: ChangeEvent<HTMLInputElement>) => {
    if (event.target.files && event.target.files[0]) {
      // check for the same type of file in the fileUploadQueue and if it exists remove it
      const file = event.target.files[0];
      const fileHash = await hashFile(file);

      dispatch(
        removeFileFromUploadQueueByCategory(IAttachmentCategory.postThumbnail)
      );

      dispatch(
        addFileToUploadQueue({
          parentId: projectEditorData._id,
          parentType: AttachmentParentType.POST,
          category: IAttachmentCategory.postThumbnail,
          resourceType: AttachmentType.image,
          fileName: file.name,
          fileHash: fileHash.toString(),
          fileSize: file.size,
          preview: URL.createObjectURL(file),
        })
      );
    }
  };

  const handleRemoveFile = async () => {
    setPreview(undefined);
    const thumbnail = projectEditorData.fileUploadQueue.find(
      (file) => file.category === IAttachmentCategory.postThumbnail
    );
    dispatch(
      removeFileFromUploadQueue({
        fileHash: thumbnail?.fileHash,
        category: IAttachmentCategory.postThumbnail,
      })
    );

    // If the thumbnail exists in server, delete it
    if (!projectEditorData?.thumbnail?._id) return;
    await deleteAttachment({
      attachmentId: projectEditorData?.thumbnail?._id,
    });
    dispatch(
      setProjectEditorData({
        ...projectEditorData,
        thumbnail: null,
      })
    );
  };

  const uploadQueueThumbnail = projectEditorData.fileUploadQueue.find(
    (file) => file.category === IAttachmentCategory.postThumbnail
  );

  const getThumbnailName = (): string => {
    if (uploadQueueThumbnail) {
      return uploadQueueThumbnail?.fileName;
    }

    return projectEditorData?.thumbnail?.fileName || '';
  };

  const thumbnail = projectEditorData.thumbnail || uploadQueueThumbnail;

  return (
    <div className="space-y-6">
      <SectionHeader
        title="Basic Details"
        description="Enter basic details about your model"
      />
      <div className="space-y-4">
        <Form {...uploadPostForm}>
          <form>
            <div className="flex flex-col py-2 gap-4">
              <div className="flex flex-col gap-2">
                <FormField
                  control={uploadPostForm.control}
                  name="title"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Title</FormLabel>
                      <FormControl>
                        <Input
                          {...field}
                          type="text"
                          placeholder="Enter title"
                          onChange={handleInputChange}
                          value={projectEditorData.title}
                        />
                      </FormControl>
                      <ErrorMessage error={errors.title} />
                    </FormItem>
                  )}
                />
              </div>

              <div className="flex flex-col gap-2">
                <FormField
                  control={uploadPostForm.control}
                  name="description"
                  render={({ field }) => (
                    <FormItem>
                      <FormLabel>Description</FormLabel>
                      <FormControl>
                        <Textarea
                          {...field}
                          placeholder="Enter description"
                          onChange={handleInputChange}
                          value={projectEditorData.description}
                        />
                      </FormControl>
                      <ErrorMessage error={errors.description} />
                    </FormItem>
                  )}
                />
              </div>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                <div className="flex flex-col gap-2">
                  <FormField
                    control={uploadPostForm.control}
                    name="category"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Category</FormLabel>
                        <FormControl>
                          <Select
                            options={getPostCategoryOptions() as any}
                            onChange={(newValue) =>
                              handleCategoryChange({
                                ...newValue,
                              } as unknown as { value: string })
                            }
                            isLoading={isLoadingPostCategories}
                            isSearchable
                            name={field.name}
                            value={
                              projectEditorData.category
                                ? {
                                    label:
                                      categories?.find(
                                        (cat) =>
                                          cat._id === projectEditorData.category
                                      )?.name || '',
                                    value: projectEditorData.category,
                                  }
                                : null
                            }
                          />
                        </FormControl>
                        <ErrorMessage error={errors.category} />
                      </FormItem>
                    )}
                  />
                </div>

                <div className="flex flex-col gap-2">
                  <FormField
                    control={uploadPostForm.control}
                    name="tags"
                    render={({ field }) => (
                      <FormItem>
                        <FormLabel>Tags</FormLabel>
                        <FormControl>
                          <CreatableSelect
                            isMulti
                            options={getTagOptions()}
                            isLoading={isTagsLoading}
                            name={field.name}
                            onCreateOption={handleCreateTag}
                            onChange={(newValue) =>
                              handleTagChange(newValue as any)
                            }
                            value={projectEditorData?.tags
                              .map((tagId) => {
                                const tag = postTags?.find(
                                  (t) => t._id === tagId
                                );
                                return tag
                                  ? { label: tag.name, value: tag._id }
                                  : null;
                              })
                              .filter(Boolean)}
                          />
                        </FormControl>
                        <ErrorMessage error={errors.tags} />
                      </FormItem>
                    )}
                  />
                </div>
              </div>
              <FormLabel>Thumbnail</FormLabel>
              <div>
                <DropZone
                  onDrop={handleFileChange}
                  preview={preview}
                  setPreview={setPreview}
                >
                  <Upload className="mx-auto h-10 w-10 text-muted-foreground" />
                  <div className="mt-4">
                    <p className="text-sm text-muted-foreground">
                      Select a file or drag and drop here
                    </p>
                    <p className="text-xs text-muted-foreground mt-1">
                      Images, Vectors, Documents
                    </p>
                    <p className="text-xs mt-1 font-semibold">
                      Make sure select your display image first
                    </p>
                  </div>
                  <Input
                    type="file"
                    className="hidden"
                    onChange={handleFileChange}
                    accept={imageExtensions.join(',')}
                    id="file-upload"
                  />
                  <Button
                    variant="outline"
                    type="button"
                    className="mt-4 border-gradient border-2"
                    onClick={(e) => {
                      e.preventDefault();
                      document.getElementById('file-upload')?.click();
                    }}
                  >
                    Select file
                  </Button>
                  <ErrorMessage error={errors.thumbnail} />
                </DropZone>
              </div>
              {thumbnail && (
                <div className="flex flex-col gap-4 max-h-56 overflow-auto">
                  <div className="space-y-4">
                    <Button
                      variant="outline"
                      type="button"
                      className="flex gap-2 py-2 px-4 rounded-sm w-full"
                      onClick={(e) => {
                        e.stopPropagation();
                        if (projectEditorData?.thumbnail) {
                          setPreview(projectEditorData.thumbnail?.url);
                        } else if (uploadQueueThumbnail) {
                          setPreview(uploadQueueThumbnail?.preview);
                        }
                      }}
                    >
                      <FileImage className="text-[#0F91D2]" />
                      <div className="flex-1 truncate">
                        {getThumbnailName()}
                      </div>
                      <Button
                        variant="ghost"
                        type="button"
                        className="rounded-full cursor-pointer"
                        onClick={(e) => {
                          e.stopPropagation();
                          handleRemoveFile();
                        }}
                      >
                        {isDeleteAttachmentLoading ? (
                          <Loader2 className="h-3 w-3 animate-spin" />
                        ) : (
                          <X className="h-3 w-3" />
                        )}
                      </Button>
                    </Button>
                  </div>
                </div>
              )}
            </div>
          </form>
        </Form>
      </div>
    </div>
  );
}

export default BasicDetails;
